import { ensureTrailingSlash } from '../url'
import { StepUpError } from './errors'

export interface InitiateStepUpParams {
  authority: string
  accessToken: string
  signal?: AbortSignal
}

export interface InitiateStepUpResult {
  method: 'totp' | 'email_otp'
  resendCooldownSeconds: number | null
}

export interface SubmitStepUpCodeParams {
  authority: string
  clientId: string
  subjectToken: string
  otpCode: string
  signal?: AbortSignal
}

export interface SubmitStepUpCodeResult {
  accessToken: string
  scope?: string
}

interface TokenPayload {
  access_token?: string
  scope?: string
  error?: string
  error_description?: string
}

const INITIATE_PATH = 'step-up/initiate'
const TOKEN_PATH = 'connect/token'

// A response body isn't guaranteed to be JSON even when the endpoint always answers with JSON:
// a gateway 502/504 serves an HTML page, a WAF can answer 429 with plain text, and an error
// response may have no body at all. Parsing must not be able to turn those into "the request
// could not be sent" — the status code is still the meaningful signal.
async function readJson<T>(response: Response): Promise<T | undefined> {
  try {
    return (await response.json()) as T
  } catch {
    return undefined
  }
}

export async function initiateStepUp(params: InitiateStepUpParams): Promise<InitiateStepUpResult> {
  const url = new URL(INITIATE_PATH, ensureTrailingSlash(params.authority))

  let response: Response
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${params.accessToken}` },
      signal: params.signal,
    })
  } catch {
    throw new StepUpError('network_error', 'initiateStepUp: the request could not be sent.')
  }

  if (response.status === 401) {
    throw new StepUpError('invalid_access_token', 'initiateStepUp: the access token is invalid or expired.')
  }
  if (response.status === 403) {
    throw new StepUpError('not_enabled', 'initiateStepUp: the step-up grant is not enabled for this client.')
  }
  if (response.status === 429) {
    throw new StepUpError('rate_limited', 'initiateStepUp: too many requests.')
  }
  if (!response.ok) {
    throw new StepUpError('network_error', `initiateStepUp: unexpected response (${response.status}).`)
  }

  const payload = await readJson<InitiateStepUpResult>(response)
  if (payload?.method !== 'totp' && payload?.method !== 'email_otp') {
    throw new StepUpError('network_error', 'initiateStepUp: the response body was not in the expected format.')
  }

  return payload
}

export async function submitStepUpCode(params: SubmitStepUpCodeParams): Promise<SubmitStepUpCodeResult> {
  const url = new URL(TOKEN_PATH, ensureTrailingSlash(params.authority))
  const body = new URLSearchParams({
    grant_type: 'step-up',
    client_id: params.clientId,
    subject_token: params.subjectToken,
    otp_code: params.otpCode,
  })

  let response: Response
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
      signal: params.signal,
    })
  } catch {
    throw new StepUpError('network_error', 'submitStepUpCode: the request could not be sent.')
  }

  const payload = (await readJson<TokenPayload>(response)) ?? {}

  if (response.ok && payload.access_token) {
    return { accessToken: payload.access_token, scope: payload.scope }
  }

  // The token endpoint intentionally returns the same "invalid_grant" for a wrong code, an
  // expired/invalid subject_token, and a client mismatch — OAuth's error taxonomy is coarse by
  // design here, to avoid turning the token endpoint into an oracle for probing stolen tokens.
  // The SDK can't and shouldn't try to tell these apart; both surface as a retryable "wrong code".
  if (payload.error === 'invalid_grant') {
    throw new StepUpError('invalid_code', payload.error_description ?? 'The provided code is invalid.')
  }
  // 429 covers the same thing coming from an intermediary (WAF, ingress rate limiter) rather
  // than from the token endpoint itself, where the body is usually not the OAuth error JSON.
  if (payload.error === 'temporarily_unavailable' || response.status === 429) {
    throw new StepUpError('rate_limited', payload.error_description ?? 'Too many attempts. Please try again later.')
  }

  throw new StepUpError('network_error', `submitStepUpCode: unexpected response (${response.status}).`)
}
